import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronUp } from 'lucide-react';
import Footer from '../components/Footer';
import ScrollIndicator from '../components/ScrollIndicator';

const JobCard = ({ title, type, location, description, requirements }) => {
  const position = (title || '').toLowerCase().replace(/ /g, '-');
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition duration-300">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-xl font-semibold mb-2">{title}</h3>
          <div className="flex flex-wrap gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              type === 'Internship' 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {type}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {location}
            </span>
          </div>
        </div>
        <Link
          to={`/apply/${position}`}
          className="inline-flex items-center text-blue-500 hover:text-blue-600 font-semibold"
        >
          Apply Now
          <ChevronRight className="w-5 h-5 ml-1" />
        </Link>
      </div>
      <p className="text-slate-600 mb-4">{description}</p>
      <div>
        <h4 className="font-semibold mb-2">Requirements:</h4>
        <ul className="space-y-2">
          {requirements.map((req, index) => (
            <li key={index} className="flex items-start text-slate-600">
              <ChevronRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
              <span className="ml-2">{req}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Careers = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowScrollTop(scrollTop > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const fullTimeJobs = [
    {
      title: "Project Manager",
      type: "Full-time",
      location: "Remote",
      description: "We're looking for an experienced Project Manager to lead our development teams and ensure successful delivery of custom software solutions for our clients.",
      requirements: [
        "5+ years of experience in project management, preferably in software development",
        "Strong understanding of Agile methodologies",
        "Excellent communication and leadership skills",
        "Experience with project management tools (Jira, Trello, etc.)",
        "PMP certification is a plus"
      ]
    },
    {
      title: "Python Developer",
      type: "Full-time",
      location: "Remote",
      description: "Join our automation team to build custom solutions, chatbots, and process automation tools that help businesses streamline their operations.",
      requirements: [
        "3+ years of experience with Python development",
        "Experience with Discord and Telegram bot development",
        "Knowledge of automation frameworks and libraries",
        "Understanding of RESTful APIs and websockets",
        "Experience with database design and SQL"
      ]
    },
    {
      title: "Algorithmic Trading Developer",
      type: "Full-time",
      location: "Remote",
      description: "Help build sophisticated trading algorithms and backtesting systems for financial markets, focusing on performance and reliability.",
      requirements: [
        "3+ years of experience in algorithmic trading development",
        "Strong understanding of financial markets and trading concepts",
        "Proficiency in Python and its trading/data analysis libraries",
        "Experience with broker APIs and real-time data processing",
        "Knowledge of risk management and portfolio optimization"
      ]
    }
  ];

  const internships = [
    {
      title: "Python Development Intern",
      type: "Internship",
      location: "Remote",
      description: "Learn and contribute to our automation projects while gaining hands-on experience with Python development, bot creation, and automation tools.",
      requirements: [
        "Currently pursuing a degree in Computer Science or related field",
        "Strong foundation in Python programming",
        "Basic understanding of APIs and web services",
        "Eager to learn about automation and bot development",
        "Good problem-solving skills and attention to detail"
      ]
    },
    {
      title: "Trading Systems Intern",
      type: "Internship",
      location: "Remote",
      description: "Join our trading systems team to learn about algorithmic trading, market analysis, and trading automation while contributing to real projects.",
      requirements: [
        "Currently pursuing a degree in Computer Science, Finance, or related field",
        "Basic understanding of financial markets and trading concepts",
        "Programming experience in Python",
        "Interest in quantitative analysis and algorithmic trading",
        "Strong analytical and mathematical skills"
      ]
    },
    {
      title: "Automation Engineering Intern",
      type: "Internship",
      location: "Remote",
      description: "Work on cutting-edge automation projects, helping businesses streamline their operations through custom solutions and integrations.",
      requirements: [
        "Currently pursuing a degree in Computer Science or related field",
        "Basic knowledge of automation concepts and tools",
        "Experience with Python programming",
        "Understanding of databases and API integration",
        "Strong desire to learn and grow in automation engineering"
      ]
    }
  ];

  return (
    <div className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-container">
      {/* Hero Section */}
      
      <section className="relative bg-slate-900 text-white min-h-[90vh] snap-start flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-slate-900/0"></div>
        <div className="container-width relative flex-1 flex flex-col justify-center items-center py-16 sm:py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">We're Expanding</h1>
            <p className="text-xl text-slate-300 mb-8">
Although humans are expensive.. But until we perfect AI, we’re hiring.
            </p>
          </div>
        </div>
        <ScrollIndicator text="Explore Opportunities" />
      </section>

      {/* Full-time Jobs Section */}
      <section className="min-h-screen snap-start bg-white flex items-center py-24">
        <div className="container-width">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8">Full-time Positions</h2>
          <div className="space-y-6">
            {fullTimeJobs.map((job, index) => (
              <JobCard key={index} {...job} />
            ))}
          </div>
        </div>
      </section>

      {/* Internships Section */}
      <section className="min-h-screen snap-start bg-slate-50 flex items-center py-24">
        <div className="container-width">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8">Internship Opportunities</h2>
          <div className="space-y-6">
            {internships.map((internship, index) => (
              <JobCard key={index} {...internship} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA & Footer Section */}
      <section className="snap-start">
        <div className="bg-white py-24">
          <div className="container-width text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Don't see a position that fits?</h2>
            <p className="text-slate-600 mb-6">
              We're always looking for talented individuals to join our team. Send us your resume and let us know how you can contribute.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Contact Us
            </Link>
          </div>
        </div>
        <Footer />
      </section>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

export default Careers;